import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Award, Plus, Trophy } from "lucide-react";
import { toast } from "sonner";

const BADGES = ["🏆", "🚀", "❤️", "⭐", "🎯", "💪", "🧠", "🔥"];

export default function Kudos() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [board, setBoard] = useState({ leaderboard: [] });
  const [open, setOpen] = useState(false);
  const load = () => { api.get("/kudos").then(({ data }) => setRows(data)); api.get("/kudos/leaderboard").then(({ data }) => setBoard(data)); };
  useEffect(() => { load(); api.get("/users").then(({ data }) => setUsers(data)); }, []);
  return (
    <div className="space-y-6" data-testid="kudos-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Recognition</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Kudos</h1>
          <p className="text-sm text-slate-400 mt-1">Celebrate wins. Points reset every month.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-kudo" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}><Plus size={16} /> Send kudos</button>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          <h2 className="text-xs uppercase tracking-widest text-slate-400">Recent</h2>
          {rows.map((k) => (
            <div key={k.id} className="card-surface p-4 flex items-start gap-3" data-testid={`kudo-${k.id}`}>
              <div className="text-3xl">{k.badge}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm"><strong className="text-[color:var(--accent)]">{k.from_name}</strong> → <strong className="text-slate-200">{k.to_name}</strong> <span className="text-slate-500">· +{k.points} pts</span></div>
                <div className="text-slate-300 mt-1">{k.message}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{new Date(k.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="card-surface p-10 text-center text-slate-500"><Award size={24} className="inline mr-2" /> No kudos yet — send the first one!</div>}
        </div>
        <aside className="card-surface p-4">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-[color:var(--accent)]"><Trophy size={14} /> This month's leaderboard</div>
          <ol className="space-y-2">
            {board.leaderboard.map((u, i) => (
              <li key={u.user_id} className="flex items-center gap-3 text-sm">
                <div className="w-6 text-center font-bold text-slate-500">{i + 1}</div>
                <div className="flex-1 truncate">{u.name}</div>
                <div className="text-[color:var(--accent)] font-bold">{u.points} pts</div>
              </li>
            ))}
            {board.leaderboard.length === 0 && <li className="text-xs text-slate-500">Nobody yet.</li>}
          </ol>
        </aside>
      </div>
      {open && <NewKudo users={users.filter((u) => u.id !== user.id)} onClose={() => setOpen(false)} onDone={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function NewKudo({ users, onClose, onDone }) {
  const [f, setF] = useState({ to_id: users[0]?.id || "", message: "", badge: "🏆", points: 3 });
  const submit = async (e) => { e.preventDefault(); await api.post("/kudos", f); onDone(); toast.success("Kudos sent!"); };
  const inp = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="kudo-modal">
        <h2 className="text-xl font-bold mb-4">Send kudos</h2>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Recipient</div><select value={f.to_id} onChange={(e) => setF({ ...f, to_id: e.target.value })} data-testid="kudo-to" required className={inp}>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Message</div><textarea rows={3} required maxLength={280} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="kudo-msg" className={inp + " resize-none"} /></label>
        <div className="mb-3">
          <div className="text-xs uppercase text-slate-500 mb-1">Badge</div>
          <div className="flex gap-2 flex-wrap">
            {BADGES.map((b) => (
              <button type="button" key={b} onClick={() => setF({ ...f, badge: b })} data-testid={`kudo-badge-${b}`} className={`w-10 h-10 rounded-lg text-2xl border ${f.badge === b ? "border-[color:var(--accent)] bg-[#ff000012]" : "border-slate-700 hover:border-slate-500"}`}>{b}</button>
            ))}
          </div>
        </div>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Points (1-5)</div><input type="range" min={1} max={5} value={f.points} onChange={(e) => setF({ ...f, points: Number(e.target.value) })} data-testid="kudo-points" className="w-full accent-[color:var(--accent)]" /><div className="text-center text-sm mt-1 text-[color:var(--accent)] font-bold">{f.points} pts</div></label>
        <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button><button type="submit" data-testid="kudo-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Send</button></div>
      </form>
    </div>
  );
}
